// A room's bench in the UI: its panel (one body, whatever hosts it) and its one
// removal, the Bench tool's and Delete's. Both go through the bench's adapter
// (roomBench.ts `roomBenchOf`), the room re-derived after every write.
// docs/claude/benches.md, *One bench, whatever hosts it*.
import {
  BENCH_TIER_COLORS,
  benchDepthFromSlats,
  STEAM_BENCH_TIER_COLORS,
} from '../benches';
import { profileExtent } from '../benchProfile';
import { benchHeadroomNote } from '../ceiling';
import { ceilingNoteText } from './ceilingUi';
import { state } from './core';
import { benchHeight } from '../heights';
import { benchTuckedUnder } from '../polyRoom';
import { tr } from '../i18n';
import {
  colorRow,
  infoRow,
  noteRow,
  numberRow,
  sectionTitle,
} from './panelRows';
import {
  type BenchPatch,
  roomBenchOf,
} from '../roomBench';
import { appendBenchEndsCard } from './roomFixtures';
import { appendBenchRoundCard } from './benchRoundTool';
import {
  dropFeature,
  featureOf,
} from '../selection';
import {
  appendSkirtingCard,
  buildProfileSection,
  buildSlatSection,
} from './slatTables';
import { type ProductInstance } from '../../shared/types';
import {
  editRoom,
  regenerateSauna,
} from '../main';

/** A link under a field that puts its default back. */
function resetLink(text: string, onClick: () => void): HTMLElement {
  const b = document.createElement('button');
  b.className = 'link-btn';
  b.textContent = text;
  b.addEventListener('click', onClick);
  return b;
}

/** The selected bench's panel — ONE body for a bench on a wall and on a
 *  partition: its wall, its depth (read-only when its slats or its profile set
 *  it), a steam room's profile, its slats, its seat height, the headroom left
 *  under the ceiling, its colour, its skirting and its ends. Every field is one
 *  undo step writing through the adapter, the room re-derived (`editRoom`); the
 *  colour picker records its own step (`colorRow`). */
export function renderBenchPanel(body: HTMLElement): boolean {
  const sel = featureOf(state, 'bench');
  if (!sel) return false;
  const room = state.drawing.instances.find((i) => i.id === sel.instId);
  const b = room ? roomBenchOf(room, sel.benchId) : null;
  if (!room || !b) {
    dropFeature(state, 'bench', 'heaterWall', 'innerWall'); // a stale selection
    return false;
  }
  const leg = b.leg;
  const steam = room.category === 'steamroom';
  const edit = (patch: BenchPatch): void => { editRoom(room, () => b.write(patch)); };
  body.appendChild(sectionTitle('Bench'));
  body.appendChild(infoRow('Wall', b.host.kind === 'wall' ? `${tr('Wall')} ${b.host.edge + 1}` : tr('Inner wall')));
  const setDepth = leg.profile ? profileExtent(leg.profile).width : benchDepthFromSlats(leg);
  if (setDepth != null) body.appendChild(infoRow('Depth (mm)', String(Math.round(setDepth))));
  else body.appendChild(numberRow('Depth (mm)', Math.round(leg.depth), (v) => edit({ depth: v }), { commit: true }));
  if (steam) buildProfileSection(body, room, leg);
  // A steam bench is a profile swept along its run (one given slats before
  // profiles existed keeps its slat editor); a sauna's carries slats.
  const slatted = !leg.profile && (!steam || !!leg.slats?.length);
  if (slatted) buildSlatSection(body, leg, b.tierIndex, edit, benchTuckedUnder(room, b.id));
  if (slatted && b.tierIndex > 0 && leg.overlap != null) body.appendChild(resetLink(tr('Use default overlap'), () => edit({ overlap: undefined })));
  const height = benchHeight({ tier: b.level, height: leg.height }, { benchTierHeights: room.benchTierHeights });
  if (leg.profile) body.appendChild(infoRow('Seat height (mm)', String(Math.round(height + b.lift))));
  else body.appendChild(numberRow('Height (mm)', Math.round(height), (v) => edit({ height: v }), { commit: true }));
  if (leg.height != null && !leg.profile) body.appendChild(resetLink(tr('Use default height'), () => edit({ height: undefined })));
  const headroom = benchHeadroomNote(room, b.id);
  if (headroom) body.appendChild(noteRow(ceilingNoteText(headroom)!, 'warn', true));
  const tierColor = (steam ? STEAM_BENCH_TIER_COLORS : BENCH_TIER_COLORS)[b.level];
  body.appendChild(colorRow('Colour', leg.color ?? room.benchColor ?? tierColor, (hex) => {
    b.write({ color: hex });
    regenerateSauna(room);
  }));
  if (leg.color) body.appendChild(resetLink(tr('Use default colour'), () => edit({ color: undefined })));
  if (slatted && leg.slats?.length) {
    appendSkirtingCard(body, leg, edit, true);
    appendBenchEndsCard(body, room, b.id, leg);
  }
  appendBenchRoundCard(body, room, b.id, b.host.kind === 'wall');
  return true;
}

/** Take a room's bench away — the Bench tool's click and Delete both: out of its
 *  host, its joints re-keyed (`RoomBench.remove`), the room re-derived, and the
 *  bench — with a partition or heater wall picked beside it — let go. Inside the
 *  caller's undo step. */
export function removeRoomBench(room: ProductInstance, id: string): void {
  roomBenchOf(room, id)?.remove();
  regenerateSauna(room);
  dropFeature(state, 'bench', 'heaterWall', 'innerWall');
}
